import type { Observable } from 'rxjs';
import { defer } from 'rxjs';
import { isEmpty } from 'lodash-es';
import type { ExtendedFetch, ExtendedFetchOpts } from '@bayer-int/portletce-shared';

import { createHttpError, createUnknownError } from '../errors';

// we actually want the any-non primitive type here, we do not inspect it anyway
type PlainRequestBody = object;

interface Query {
    [key: string]: string | number | Array<string | number>;
}

export interface RequestParameters extends Omit<RequestInit, 'body'>, ExtendedFetchOpts {
    body?: RequestInit['body'] | PlainRequestBody;
    responseType?: 'json' | 'text' | 'arrayBuffer' | 'blob' | 'formData';
    query?: Query;
}

export interface HttpResponse<T = unknown> {
    headers: Record<string, string>;
    status: number;
    url: string;
    data: T;
}

export class HttpService {
    constructor(private readonly fetchFunc: ExtendedFetch) {}

    public get<T = unknown>(
        url: string,
        params: RequestParameters = {},
    ): Observable<HttpResponse<T>> {
        return this.makeRequest(url, { ...params, method: 'GET' });
    }

    public post<T = unknown>(
        url: string,
        params: RequestParameters = {},
    ): Observable<HttpResponse<T>> {
        return this.makeRequest(url, { ...params, method: 'POST' });
    }

    public put<T = unknown>(
        url: string,
        params: RequestParameters = {},
    ): Observable<HttpResponse<T>> {
        return this.makeRequest(url, { ...params, method: 'PUT' });
    }

    public delete<T = unknown>(
        url: string,
        params: RequestParameters = {},
    ): Observable<HttpResponse<T>> {
        return this.makeRequest(url, { ...params, method: 'DELETE' });
    }

    private makeRequest<T = unknown>(
        url: string,
        params: RequestParameters = {},
    ): Observable<HttpResponse<T>> {
        const {
            body,
            responseType = 'json',
            memoryCache,
            retryCount,
            headers,
            query,
            context,
            ...rest
        } = params;
        const init: RequestInit = {
            ...rest,
            headers: HttpService.extendHeaders(headers, params),
            body: HttpService.formatBody(body),
        };
        const extensions: ExtendedFetchOpts = { memoryCache, retryCount, context };
        const urlWithQuery = HttpService.attachQueryToUrl(url, query);

        return defer(() =>
            this.fetchFunc(urlWithQuery, init, extensions)
                .then(response => {
                    const dataPromise =
                        response.status === 204 || response.status === 202
                            ? Promise.resolve(null)
                            : response.ok
                              ? response[responseType]()
                              : Promise.reject(createHttpError(response.status, response));

                    return dataPromise.then(data => ({
                        status: response.status,
                        url: response.url,
                        headers: HttpService.formatHeaders(response.headers),
                        data,
                    }));
                })
                .catch(err => Promise.reject(createUnknownError(err))),
        );
    }

    private static formatBody(body: RequestParameters['body']): RequestInit['body'] {
        return HttpService.isPlainRecord(body) ? JSON.stringify(body) : body;
    }

    private static extendHeaders(
        headers: RequestParameters['headers'],
        parameters: RequestParameters,
    ): RequestParameters['headers'] {
        return {
            ...headers,
            ...(HttpService.isPlainRecord(parameters.body)
                ? { 'Content-Type': 'application/json' }
                : {}),
        };
    }

    private static formatHeaders(headers: Headers): Record<string, string> {
        return [...headers.entries()].reduce(
            (acc, [key, value]) => {
                acc[key] = value;
                return acc;
            },
            {} as Record<string, string>,
        );
    }

    private static isPlainRecord(a: unknown): a is PlainRequestBody {
        if (a === null || a === undefined) {
            return false;
        }
        return typeof a === 'object' && !Array.isArray(a);
    }

    private static objectToQueryString(obj: Query): string {
        return Object.entries(obj)
            .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(',') : value}`)
            .join('&');
    }

    private static attachQueryToUrl(url: string, query?: Query): string {
        return query && !isEmpty(query) ? `${url}?${HttpService.objectToQueryString(query)}` : url;
    }
}
